/** @jsxImportSource @emotion/react */

import { Autocomplete } from "components/Autocomplete";
import { Button } from "components/Button";
import { Card } from "components/Card";
import { Input } from "components/Input";
import { Page } from "components/Page";
import {
  NOTIFICATION_TYPE,
  NotificationContext,
} from "context/NotificationContext";
import { UserContext } from "context/UserContext";
import { useContext, useEffect, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { ReactTabulator } from "react-tabulator";
import { AliRecord, getBulkChangeRequestColumns } from "services/ALIRecord.d";
import { AliChange } from "services/DiscrepancyType";
import RecordService from "services/RecordService";
import tw from "twin.macro";
import { updateFooterText } from "utils/helpers";
import { getOptionsGeneric } from "./ALIForm";

const client = new RecordService("ali");

function getUniqueValues(request: any, records: any[], key: string) {
  const uniqueValues = {};

  Object.keys(request).forEach((key) => {
    if (key === "Notes") return;

    //remove EDIT_ prefix
    const noPrefix = key.replace("EDIT_", "");

    const values = records.map((r) => r[noPrefix] || "");
    const uniqueSet = new Set(values);

    if (uniqueSet.size <= 1) {
      uniqueValues[key] = values[0] || "";
    } else {
      uniqueValues[key] = "MULTIPLE VALUES";
    }
  });

  return uniqueValues[key];
}

const discrepClient = new RecordService("discrep");

const BulkChangeRequest = () => {
  const { user } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext);

  const [pageSize, setPageSize] = useState<number>(10);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  const [aliCols] = useState(
    getBulkChangeRequestColumns(user?.functions || []),
  );

  const [affectedAliRecords, setAffectedAliRecords] = useState<AliRecord[]>([]);
  const [criteria, setCriteria] = useState<any[]>([]);

  const tableRef = useRef<any>(null);
  // set tabulator options
  const options = {
    layout: "fitColumns",
    movableColumns: true,
    placeholder: "No ALI Records Found",
    // persist table state
    persistenceID: "aliRecords1", // update ID param whenever table columns are updated
    persistenceMode: true, // store table state in local storage if possible, if not store in cookie
    persistence: {
      columns: ["width"], // persist column width; also persists column order
      page: true, // persist page number
    },
    // pagination
    pagination: true, // enable pagination
    paginationMode: "remote", // enable remote pagination
    paginationSize: pageSize, // default number of rows per page
    paginationSizeSelector: [10, 25, 50, 100], // page size options
    footerElement: "<span id='record-count'></span>",
    // filter
    filterMode: "remote", // send filter data to the server instead of processing locally
    // sorting
    sortMode: "remote",
    initialSort: [{ column: "CPN", dir: "asc" }],
    // data request
    ajaxRequestFunc: function (url, config, params) {
      // convert default table params to API params
      let sortField = params.sort.length > 0 ? params.sort[0].field : "CPN";
      let sortDir = params.sort.length > 0 ? params.sort[0].dir : "asc";
      let firstRow = (params.page - 1) * params.size;
      let rangeStr = firstRow + "," + params.size;

      let savedCriterias = localStorage.getItem("aliRecords-criteria");
      let crit: Array<{ field: string; type: string; value: string }> = [];
      if (savedCriterias) crit = JSON.parse(savedCriterias);

      let reqBody = {
        range: rangeStr,
        sortBy: sortField,
        sortDirection: sortDir,
        criterias: [
          ...crit,
          ...params.filter.map((f) => ({
            field: f.field,
            type: f.type,
            value: f.value,
          })),
        ],
        full: true,
      };

      setCriteria(reqBody.criterias);

      // log request body
      console.log("Bulk Change Request table body", reqBody);
      return new Promise(function (resolve, reject) {
        // fetch records
        client
          .getRecords(reqBody, url)
          .then((res) => {
            // success -> return data
            resolve(res.data);
          })
          .catch((err) => {
            // error -> return error message
            console.error("Bulk Change Request table error response", err);
            reject(err);
          });
      });
    },
    ajaxURL: "searchByCriteria", // API path
    ajaxResponse: (url, params, response) => {
      // log response
      console.log("Bulk Change Request table response", response);
      // pagination footer text
      let currPageSize = tableRef.current.getPageSize();
      let currPage = tableRef.current.getPage();
      updateFooterText(
        currPage,
        currPageSize,
        response.message.size,
        response.message.totalRecordCount,
      );
      setRecordCount(response.message.totalRecordCount);
      setLoading(false);
      setAffectedAliRecords(response.message.list);

      // return data to table
      let d = response.message.size > 0 ? response.message.list : [];
      let last =
        response.message.size > 0
          ? Math.ceil(response.message.totalRecordCount / currPageSize)
          : 0;
      return {
        data: d,
        last_page: last,
      };
    },
  };

  const [recordRequest, setRecordRequest] = useState<AliChange>({
    EDIT_STA: "",
    EDIT_MCN: "",
    EDIT_STN: "",
    EDIT_STS: "",
    EDIT_PRD: "",
    EDIT_POD: "",
    EDIT_HNO: "",
    EDIT_HNS: "",
    EDIT_LOC: "",
    EDIT_Comments: "",
    EDIT_NAM: "",
    NOTES: "",
  });

  useEffect(() => {
    const updatedRequest = { ...recordRequest };
    Object.keys(recordRequest).forEach((key) => {
      const uniqueValue = getUniqueValues(
        recordRequest,
        affectedAliRecords,
        key,
      );
      if (uniqueValue !== undefined) updatedRequest[key] = uniqueValue;
    });
    setRecordRequest(updatedRequest);
  }, [affectedAliRecords]);

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [step, setStep] = useState<number>(1);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target as
      | HTMLCalciteInputElement
      | HTMLCalciteTextAreaElement;
    setRecordRequest({
      ...recordRequest,
      [name]: value,
    });
  };

  async function handleSubmit() {
    setIsSaving(true);
    try {
      // remove all the fields in recordRequest that are MULTIPLE VALUES to set as their original values
      let filteredRequest = { ...recordRequest };
      Object.keys(filteredRequest).forEach((key) => {
        if (filteredRequest[key] === "MULTIPLE VALUES") {
          delete filteredRequest[key];
        }
      });

      const res = await discrepClient.createBulkChangeRequest(
        criteria,
        filteredRequest,
      );

      if (res.status === 200) {
        let idString = "";
        if (res.data?.message?.IDs) {
          const ids = res.data.message.IDs;
          if (ids.length === 1) {
            idString = `Discrepancy ${ids[0]} created`;
          } else if (ids.length === 2) {
            idString = `Discrepancies ${ids[0]} and ${ids[1]} created`;
          } else if (ids.length > 2) {
            idString = `Discrepancies ${ids[0]} through ${ids[ids.length - 1]} created`;
          }
        }

        addNotification({
          title: idString,
          message: "Your changes have been submitted.",
          type: NOTIFICATION_TYPE.SUCCESS,
        });
        setIsSaving(false);
        setTimeout(() => {
          navigate("/ali-records");
        }, 1500);
      }
    } catch (error: any) {
      setIsSaving(false);
      addNotification({
        title: "Uh oh.",
        message: "Something went wrong: " + error?.message,
        type: NOTIFICATION_TYPE.ERROR,
      });
    }
  }

  // keep track of when each autocomplete field was last changed and use that to trigger option fetches
  const [lastFired, setLastFired] = useState<{
    mcn?: string | Date;
    streetName?: string | Date;
    streetSuffix?: string | Date;
    state?: string | Date;
  }>({
    mcn: new Date().toString(),
    streetName: new Date().toString(),
    streetSuffix: new Date().toString(),
    state: new Date().toString(),
  });

  useEffect(() => {
    setLastFired({
      mcn: new Date().toString(),
      streetName: new Date().toString(),
      streetSuffix: new Date().toString(),
      state: new Date().toString(),
    });
  }, [
    recordRequest.EDIT_MCN,
    recordRequest.EDIT_STN,
    recordRequest.EDIT_STS,
    recordRequest.EDIT_STA,
  ]);

  return (
    <Page
      parentRoute="/ali-records"
      label="ALI Records"
      breadCrumb={{
        id: "/bulk-change-request",
        title: "Bulk Change Request",
        active: true,
      }}
    >
      {!loading && recordCount > 30 && (
        <div
          css={[
            tw`bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4`,
          ]}
        >
          <div css={[tw`font-bold`]}>
            More than 30 discrepancies would be created by this request
          </div>
          <div>Please contact database@indigital.net to make these changes</div>
        </div>
      )}

      {step === 1 ? (
        <h3>Affected Records {!loading && `(${recordCount})`}</h3>
      ) : (
        <h3>Review Changes</h3>
      )}

      {step === 1 && (
        <div
          css={[
            tw`overflow-hidden min-h-full border border-gray-300 rounded-md`,
          ]}
        >
          <ReactTabulator
            id="aliLoader"
            onRef={(r) => {
              tableRef.current = r.current;
            }}
            events={{
              pageSizeChanged: function (pagesize) {
                setPageSize(pagesize);
              },
            }}
            options={options}
            columns={aliCols}
          />
        </div>
      )}
      {step === 2 && (
        <ReviewChanges
          recordRequest={recordRequest}
          affectedAliRecords={affectedAliRecords}
        />
      )}
      <Outlet />
      <Card>
        <div>
          <Card style={{ width: "100%" }}>
            {step === 1 && (
              <div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "20px",
                  }}
                >
                  {/* Left Column */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <Autocomplete
                      label="State/Province"
                      value={recordRequest.EDIT_STA || ""}
                      onChange={(value: string) =>
                        setRecordRequest((prev) => ({
                          ...prev,
                          EDIT_STA: value,
                        }))
                      }
                      optionFetch={async () => {
                        // Replace with your actual fetch logic
                        const options = await getOptionsGeneric(
                          "State",
                          recordRequest,
                        );
                        return options;
                      }}
                      lastFired={lastFired.state}
                      scale="s"
                      labelSize="s"
                    />
                    <Autocomplete
                      label="MSAG Community Name"
                      value={recordRequest.EDIT_MCN || ""}
                      onChange={(value: string) =>
                        setRecordRequest((prev) => ({
                          ...prev,
                          EDIT_MCN: value,
                        }))
                      }
                      optionFetch={async () => {
                        const options = await getOptionsGeneric(
                          "MCN",
                          recordRequest,
                        );
                        return options;
                      }}
                      lastFired={lastFired.mcn}
                      scale="s"
                      labelSize="s"
                    />
                    <Autocomplete
                      label="Street Name"
                      value={recordRequest.EDIT_STN || ""}
                      onChange={(value: string) =>
                        setRecordRequest((prev) => ({
                          ...prev,
                          EDIT_STN: value,
                        }))
                      }
                      optionFetch={async () => {
                        const options = await getOptionsGeneric(
                          "StreetName",
                          recordRequest,
                        );
                        return options;
                      }}
                      lastFired={lastFired.streetName}
                      scale="s"
                      labelSize="s"
                    />
                    <Autocomplete
                      label="Street Suffix"
                      value={recordRequest.EDIT_STS || ""}
                      onChange={(value: string) =>
                        setRecordRequest((prev) => ({
                          ...prev,
                          EDIT_STS: value,
                        }))
                      }
                      optionFetch={async () => {
                        const options = await getOptionsGeneric(
                          "StreetSuffix",
                          recordRequest,
                        );
                        return options;
                      }}
                      lastFired={lastFired.streetSuffix}
                      scale="s"
                      labelSize="s"
                    />

                    <Input
                      name="EDIT_PRD"
                      label="Prefix Directional"
                      value={recordRequest.EDIT_PRD || ""}
                      onChange={handleChange}
                      scale="s"
                      labelSize="s"
                    />
                    <Input
                      name="EDIT_POD"
                      label="Post Directional"
                      value={recordRequest.EDIT_POD || ""}
                      onChange={handleChange}
                      scale="s"
                      labelSize="s"
                    />
                    <Input
                      name="EDIT_HNO"
                      label="House Number"
                      value={recordRequest.EDIT_HNO || ""}
                      onChange={handleChange}
                      scale="s"
                      labelSize="s"
                    />
                    <Input
                      name="EDIT_HNS"
                      label="House Number Suffix"
                      value={recordRequest.EDIT_HNS || ""}
                      onChange={handleChange}
                      scale="s"
                      labelSize="s"
                    />
                  </div>

                  {/* Right Column */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <Input
                      name="EDIT_LOC"
                      label="Location"
                      value={recordRequest.EDIT_LOC || ""}
                      onChange={handleChange}
                      scale="s"
                      labelSize="s"
                    />
                    <Input
                      name="EDIT_Comments"
                      label="Comments"
                      value={recordRequest.EDIT_Comments || ""}
                      onChange={handleChange}
                      scale="s"
                      labelSize="s"
                    />
                    <Input
                      name="NOTES"
                      type="textarea"
                      label="Notes"
                      value={recordRequest.NOTES || ""}
                      onChange={handleChange}
                      scale="s"
                      labelSize="s"
                    />
                  </div>

                  <hr style={{ margin: "20px 0" }} />

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: "20px",
                      gap: "12px",
                    }}
                  >
                    <Button
                      variant="outline"
                      kind="brand"
                      label="Cancel"
                      onClick={() => navigate(-1)}
                      type="button"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      width="auto"
                      disabled={recordCount > 30}
                      onClick={() => setStep(2)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            )}
            {step === 2 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "20px",
                  gap: "12px",
                }}
              >
                <Button
                  variant="outline"
                  kind="brand"
                  label="Back"
                  onClick={() => setStep(1)}
                  type="button"
                >
                  Back
                </Button>
                <Button
                  isLoading={isSaving}
                  onClick={handleSubmit}
                  width="auto"
                  disabled={
                    isSaving
                    // ||
                    // Object.values(recordRequest).some(
                    //   (value) => value === "MULTIPLE VALUES"
                    // )
                  }
                >
                  Submit
                </Button>
              </div>
            )}
          </Card>
        </div>
      </Card>
    </Page>
  );
};

const ReviewChanges = ({ recordRequest, affectedAliRecords }) => {
  const keyMappings = {
    EDIT_STA: "State/Province",
    EDIT_MCN: "MSAG Community Name",
    EDIT_STN: "Street Name",
    EDIT_STS: "Street Suffix",
    EDIT_PRD: "Prefix Directional",
    EDIT_POD: "Post Directional",
    EDIT_HNO: "House Number",
    EDIT_HNS: "House Number Suffix",
    EDIT_LOC: "Location",
    EDIT_Comments: "Comments",
    Notes: "Notes",
  };

  return (
    <div
      css={[
        tw`col-span-2 overflow-hidden min-h-full border border-gray-400 rounded-md`,
      ]}
    >
      <table css={[tw`w-full`]}>
        <thead css={[tw`bg-gray-200 border-b border-gray-400`]}>
          <tr css={[tw`text-left`]}>
            <th css={[tw`border-r border-gray-400 p-2`]}></th>
            <th css={[tw`border-r border-gray-400 font-semibold p-2`]}>
              Original
            </th>
            <th css={[tw`font-semibold p-2`]}>Requested</th>
          </tr>
        </thead>
        <tbody>
          {[
            "EDIT_HNO",
            "EDIT_HNS",
            "EDIT_PRD",
            "EDIT_STN",
            "EDIT_STS",
            "EDIT_POD",
            "EDIT_MCN",
            "EDIT_STA",
            "EDIT_LOC",
            "EDIT_Comments",
          ].map((key) => (
            <tr key={key} css={[tw`border-b border-gray-300`]}>
              <td css={[tw`border-r border-gray-400 p-2 font-semibold`]}>
                {keyMappings[key] || key}
              </td>
              <td css={[tw`border-r border-gray-400 p-2`]}>
                {getUniqueValues(recordRequest, affectedAliRecords, key)}
              </td>
              <td css={[tw`p-2`]}>{String(recordRequest[key])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default BulkChangeRequest;
